import { Connection, EdgeProps, Node, ReactFlowInstance } from "reactflow"
import {
  edgeSourceSheetHandleRegExp,
  edgeTargetSheetHandleRegExp,
} from "./constants"
import { areCellsConnectedThroughLogic } from "./connectionValidation"
import {
  CanvasEdges,
  CanvasNodes,
  SetEdgesState,
  SetNodesState,
  UIEdge,
  UILogic,
  UILogicType,
  UISheet,
} from "../types/UITypes"
import { MutableRefObject } from "react"
import { addUILogicConnection } from "./addUILogicConnection"
import { SupabaseConnector } from "../database/SupabaseConnector"
import { addUIEdgeToLogic } from "./addUIEdgeToLogic"
import { addCellToCellConnection } from "./addCellToCellConnection"
import {
  removeCellToCellEdge,
  removeEdgeWithoutRemainingIncomingConnections,
  removeEdgeWithRemainingIncomingConnections,
  removeLogicToCellEdge,
} from "./removeCellToLogicEdge"

export async function addEdgeConnection(
  connection: Connection,
  nodes: CanvasNodes,
  edges: CanvasEdges,
  setEdges: SetEdgesState,
  setNodes: SetNodesState,
  reactFlowRef: MutableRefObject<ReactFlowInstance | null>,
  supabaseConnector: SupabaseConnector
) {
  const { source, target, sourceHandle, targetHandle } = connection

  if (!source || !target || source === target) {
    return
  }

  const sourceSheetHandleMatch = sourceHandle?.match(
    edgeSourceSheetHandleRegExp
  )

  const targetSheetHandleMatch = targetHandle?.match(
    edgeTargetSheetHandleRegExp
  )

  if (sourceSheetHandleMatch && targetSheetHandleMatch) {
    const sourceSheetId = sourceSheetHandleMatch && sourceSheetHandleMatch[1]
    const sourceRowIndex = sourceSheetHandleMatch && sourceSheetHandleMatch[2]

    const targetSheetId = targetSheetHandleMatch && targetSheetHandleMatch[1]
    const targetRowIndex = targetSheetHandleMatch && targetSheetHandleMatch[2]

    if (
      !sourceSheetId ||
      !targetSheetId ||
      !sourceRowIndex ||
      !targetRowIndex ||
      !source ||
      !target ||
      edges.some(
        (edge) =>
          edge.sourceHandle?.replace(/edge-/, "").replace(/-node-.*/, "") ===
            sourceHandle &&
          edge.targetHandle?.replace(/edge-/, "").replace(/-node-.*/, "") ===
            targetHandle
      ) ||
      areCellsConnectedThroughLogic(
        // TODO: fix FlowChart types
        // @ts-ignore
        nodes,
        edges,
        connection,
        sourceSheetId,
        sourceRowIndex,
        targetSheetId,
        targetRowIndex
      )
    ) {
      return
    }

    // handle ReactFlow edge-case
    const edgeTargethandlePreffix = targetHandle?.startsWith("edge")
      ? targetHandle
      : `edge-${targetHandle}`
    const destCellIncomingEdge = edges.find((edge) =>
      edge.targetHandle?.startsWith(edgeTargethandlePreffix)
    )

    // Add new logic connection
    if (destCellIncomingEdge?.data.sourceSheetId) {
      const { updatedNodes, updatedEdges, newLogic, newEdges } =
        addUILogicConnection(
          // TODO: fix FlowChart types
          // @ts-ignore
          nodes,
          edges,
          connection,
          sourceSheetId,
          targetSheetId,
          sourceRowIndex,
          targetRowIndex,
          destCellIncomingEdge,
          reactFlowRef.current!,
          UILogicType.AND
        )

      setEdges(updatedEdges)
      setNodes(updatedNodes)

      await supabaseConnector.addLogicConnection(
        newLogic,
        destCellIncomingEdge,
        newEdges
      )

      return { newLogic, newEdges, updatedEdges }
    } else if (destCellIncomingEdge?.data.sourceLogicId) {
      // Add connection to existing logic
      const { updatedNodes, updatedEdges, newEdge } = addUIEdgeToLogic(
        // TODO: fix FlowChart types
        // @ts-ignore
        nodes,
        edges,
        connection,
        sourceSheetId,
        sourceRowIndex,
        destCellIncomingEdge
      )

      setNodes(updatedNodes)
      setEdges(updatedEdges)

      await supabaseConnector.addEdge(newEdge)
    } else {
      // Connect cell to cell
      const { updatedNodes, updatedEdges, newEdge } = addCellToCellConnection(
        // TODO: fix FlowChart types
        // @ts-ignore
        nodes,
        edges,
        connection,
        sourceSheetId,
        targetSheetId,
        sourceRowIndex,
        targetRowIndex
      )

      setNodes(updatedNodes)
      setEdges(updatedEdges)

      await supabaseConnector.addEdge(newEdge)
    }
  }
}

export async function removeEdgeConnection(
  edgeProps: EdgeProps<UIEdge>,
  edges: CanvasEdges,
  setEdges: SetEdgesState,
  nodes: CanvasNodes,
  setNodes: SetNodesState,
  supabaseConnector: SupabaseConnector
) {
  const { source, target, sourceHandleId, targetHandleId } = edgeProps

  const { id, sourceLogicId, targetLogicId, sourceRowIndex, targetRowIndex } =
    edgeProps?.data!

  const edgeSourceNodeId = sourceHandleId?.replace("edge-", "")
  const edgeTargetNodeId = targetHandleId?.replace("edge-", "")

  if (!edgeSourceNodeId || !edgeTargetNodeId) {
    return
  }

  const sourceSheet = source.startsWith("sheet")
    ? (nodes.find(({ id }) => id === source) as Node<UISheet>)
    : undefined
  const targetSheet = target.startsWith("sheet")
    ? (nodes.find(({ id }) => id === target) as Node<UISheet>)
    : undefined
  const sourceLogic = source.startsWith("logic")
    ? (nodes.find(({ id }) => id === source) as Node<UILogic>)
    : undefined
  const targetLogic = target.startsWith("logic")
    ? (nodes.find(({ id }) => id === target) as Node<UILogic>)
    : undefined

  if (sourceSheet && targetSheet) {
    const updatedNodes = removeCellToCellEdge(
      // TODO: fix FlowChart types
      // @ts-ignore
      nodes,
      sourceSheet,
      targetSheet,
      edgeSourceNodeId,
      edgeTargetNodeId
    )

    setNodes(updatedNodes)
    setEdges((edges) => edges.filter((edge) => edge.data?.id !== id))

    supabaseConnector.removeEdge(id)
  }

  if (sourceRowIndex && targetLogicId) {
    const remainingIncomingConnections = targetLogic?.data?.edgeHandles?.filter(
      (edgeHandle) =>
        edgeHandle.nodeType === "target" && edgeHandle.id !== edgeTargetNodeId
    )

    if (!remainingIncomingConnections?.length) {
      return
    }

    if (remainingIncomingConnections.length > 1 && sourceSheet && targetLogic) {
      const updatedNodes = removeEdgeWithRemainingIncomingConnections(
        sourceSheet,
        targetLogic,
        edgeSourceNodeId,
        edgeTargetNodeId,
        // TODO: fix FlowChart types
        // @ts-ignore
        nodes
      )

      setNodes(updatedNodes)
      setEdges((edges) => edges.filter((edge) => edge.data?.id !== id))

      supabaseConnector.removeEdge(id)
    } else {
      const updatedEntitiesAfterEdgeRemoval =
        removeEdgeWithoutRemainingIncomingConnections(
          edges,
          // TODO: fix FlowChart types
          // @ts-ignore
          nodes,
          remainingIncomingConnections,
          targetLogic,
          targetLogicId,
          source,
          edgeSourceNodeId,
          id
        )

      if (!updatedEntitiesAfterEdgeRemoval) {
        return
      }

      const {
        updatedNodes,
        updatedEdges,
        removedLogicOutgoingEdge,
        edgeToUpdate,
      } = updatedEntitiesAfterEdgeRemoval

      setNodes(updatedNodes)
      setEdges(updatedEdges)

      supabaseConnector.replaceLogicWithEdge(targetLogicId, edgeToUpdate, [
        id,
        removedLogicOutgoingEdge.data.id,
      ])
    }
  }

  if (sourceLogicId && targetRowIndex) {
    const { updatedNodes, removedEdges } = removeLogicToCellEdge(
      // TODO: fix FlowChart types
      // @ts-ignore
      nodes,
      edges,
      sourceLogic,
      targetSheet,
      edgeTargetNodeId,
      sourceLogicId,
      target
    )

    setNodes(updatedNodes)
    setEdges((edges) => edges.filter((edge) => !removedEdges.includes(edge)))

    await supabaseConnector.handleRemoveLogic(
      sourceLogicId,
      removedEdges.map(({ data }) => data.id) as string[]
    )
  }
}
